import { useState, useEffect, useRef, useCallback } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import { useDashLang } from "@/context/DashboardLangContext";
import { useAuth } from "@/context/AuthContext";
import { searchReservations, type SearchHit } from "@/lib/supabase/reservations";
import { useDialog } from "@/components/ui/useDialog";

/**
 * v1.22.12 · Cmd+K / Ctrl+K universal search.
 *
 * One input, matches across every reservation field (code / name /
 * passport / phone / email / room). Top result keyboard-navigable.
 * Enter → open reservation detail. Quick-action shortcuts:
 *   C → Check-In · O → Check-Out · M → Change Room · B → Billing.
 *
 * The overlay mounts via portal, absolute-top-center, Diyafa-branded.
 */

export default function UniversalSearch() {
  const { isAr, t } = useDashLang();
  const { currentOrg } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const dialogRef = useDialog(() => setOpen(false), open);
  const [query, setQuery] = useState("");
  const [hits, setHits] = useState<SearchHit[]>([]);
  const [activeIdx, setActiveIdx] = useState(0);
  const [loading, setLoading] = useState(false);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const debounce = useRef<number | null>(null);

  // Cmd+K / Ctrl+K anywhere
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        setOpen((v) => !v);
      } else if (e.key === "Escape" && open) {
        setOpen(false);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open]);

  useEffect(() => {
    if (open) {
      setTimeout(() => inputRef.current?.focus(), 10);
      setActiveIdx(0);
    } else {
      setQuery(""); setHits([]);
    }
  }, [open]);

  useEffect(() => {
    if (!open || !currentOrg) return;
    if (debounce.current) window.clearTimeout(debounce.current);
    if (!query.trim()) { setHits([]); setLoading(false); return; }
    setLoading(true);
    debounce.current = window.setTimeout(async () => {
      try { setHits(await searchReservations(currentOrg.id, query.trim(), 8)); }
      finally { setLoading(false); setActiveIdx(0); }
    }, 150);
  }, [query, open, currentOrg]);

  const runQuickAction = useCallback((hit: SearchHit, key: string) => {
    setOpen(false);
    switch (key.toLowerCase()) {
      case "c": navigate(`/dashboard/checkin?reservationId=${hit.id}`); return;
      case "o": navigate(`/dashboard/checkout?reservationId=${hit.id}`); return;
      case "b": navigate(`/dashboard/billing?reservationId=${hit.id}`); return;
      case "m":
      default:  navigate(`/dashboard/reservations/${hit.id}`);
    }
  }, [navigate]);

  // Input key handlers
  const onInputKey = (e: React.KeyboardEvent) => {
    if (e.key === "ArrowDown") { e.preventDefault(); setActiveIdx((i) => Math.min(i + 1, hits.length - 1)); }
    else if (e.key === "ArrowUp") { e.preventDefault(); setActiveIdx((i) => Math.max(i - 1, 0)); }
    else if (e.key === "Enter" && hits[activeIdx]) {
      e.preventDefault();
      runQuickAction(hits[activeIdx], "m");
    }
    // Letter shortcuts when a hit is active (hold Alt or just type)
    else if (e.altKey && hits[activeIdx] && ["c","o","m","b"].includes(e.key.toLowerCase())) {
      e.preventDefault();
      runQuickAction(hits[activeIdx], e.key);
    }
  };

  if (!open) return null;
  if (typeof document === "undefined") return null;

  return createPortal(
    <div className="fixed inset-0 z-[70] bg-black/50 flex items-start justify-center pt-[10vh] px-4" onClick={() => setOpen(false)} dir={isAr ? "rtl" : "ltr"}>
      <div ref={dialogRef} role="dialog" aria-modal="true" tabIndex={-1} onClick={(e) => e.stopPropagation()}
        className="w-full max-w-2xl bg-white rounded-2xl shadow-2xl border border-line overflow-hidden">
        {/* Input */}
        <div className={`flex items-center gap-3 px-5 py-4 border-b border-line`}>
          <i className="ri-search-line text-gold text-xl"></i>
          <input
            ref={inputRef}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={onInputKey}
            placeholder={t(
              "Search by code, name, passport, room, phone…",
              "ابحث بالرمز، الاسم، الجواز، الغرفة، الهاتف…",
            )}
            className={`flex-1 bg-transparent text-sm outline-none ${isAr ? "font-tajawal text-right" : ""}`}
          />
          {loading && <i className="ri-loader-4-line animate-spin text-gold"></i>}
          <kbd className="hidden sm:inline-block px-1.5 py-0.5 rounded-md border border-line-strong bg-cream text-[10px] font-mono text-muted">ESC</kbd>
        </div>

        {/* Results */}
        <div className="max-h-[50vh] overflow-y-auto">
          {!query && (
            <div className={`p-5 space-y-2 ${isAr ? "text-right" : ""}`}>
              <p className={`text-[11px] font-bold text-muted uppercase tracking-wide ${isAr ? "font-tajawal" : ""}`}>
                {t("Try typing", "جرب كتابة")}
              </p>
              {[
                { icon: "ri-hashtag",          en: "HTL-2026-0003 (confirmation code)", ar: "HTL-2026-0003 (رمز الحجز)" },
                { icon: "ri-user-line",        en: "Al Balushi (surname → family)",       ar: "البلوشي (الاسم أو اللقب)" },
                { icon: "ri-passport-line",    en: "A12345678 (passport / ID)",           ar: "A12345678 (جواز / هوية)" },
                { icon: "ri-hotel-bed-line",   en: "305 (room number)",                    ar: "305 (رقم الغرفة)" },
                { icon: "ri-phone-line",       en: "+96892882801 (phone)",                 ar: "+96892882801 (الهاتف)" },
              ].map((tip, i) => (
                <div key={i} className={`flex items-center gap-3 text-xs text-muted ${isAr ? "font-tajawal" : ""}`}>
                  <i className={`${tip.icon} text-gold`}></i>
                  <span>{isAr ? tip.ar : tip.en}</span>
                </div>
              ))}
              <div className={`mt-4 pt-3 border-t border-line flex items-center justify-between gap-3 text-[10px] text-muted ${isAr ? "font-tajawal" : ""}`}>
                <span><kbd className="px-1 rounded bg-cream border border-line-strong font-mono">↵</kbd> {t("Open", "فتح")}</span>
                <span><kbd className="px-1 rounded bg-cream border border-line-strong font-mono">⌥C</kbd> {t("Check-In", "تسجيل الوصول")}</span>
                <span><kbd className="px-1 rounded bg-cream border border-line-strong font-mono">⌥O</kbd> {t("Check-Out", "المغادرة")}</span>
                <span><kbd className="px-1 rounded bg-cream border border-line-strong font-mono">⌥B</kbd> {t("Billing", "الفواتير")}</span>
              </div>
            </div>
          )}

          {query && hits.length === 0 && !loading && (
            <p className={`text-sm text-muted text-center py-8 ${isAr ? "font-tajawal" : ""}`}>
              {t("No results.", "لا توجد نتائج.")}
            </p>
          )}

          {hits.map((h, i) => {
            const active = i === activeIdx;
            return (
              <button
                key={h.id}
                onMouseEnter={() => setActiveIdx(i)}
                onClick={() => runQuickAction(h, "m")}
                className={`w-full text-start px-5 py-3 flex items-center gap-3 cursor-pointer transition-colors border-s-2 ${
                  active ? "bg-gold-soft border-gold" : "border-transparent hover:bg-cream"
                } ${isAr ? "text-right" : ""}`}
              >
                <i className={`${matchIcon(h.match_field)} text-gold text-base flex-shrink-0`}></i>
                <div className="flex-1 min-w-0">
                  <p className={`text-sm font-semibold text-ink truncate ${isAr ? "font-tajawal" : ""}`}>
                    {h.guest_name}
                    {h.guest_name_ar && <span className="text-muted font-tajawal font-normal"> · {h.guest_name_ar}</span>}
                  </p>
                  <p className="text-[11px] text-muted font-mono truncate" dir="ltr">
                    {h.confirmation_code}
                    {h.room_number && ` · Rm ${h.room_number}`}
                    {" · "}{h.check_in_date} → {h.check_out_date}
                    {" · "}{h.status.replace("_", " ")}
                  </p>
                </div>
                {active && <i className={`text-gold ${isAr ? "ri-arrow-left-line" : "ri-arrow-right-line"}`}></i>}
              </button>
            );
          })}
        </div>
      </div>
    </div>,
    document.body,
  );
}

function matchIcon(f: SearchHit["match_field"]): string {
  return {
    code: "ri-hashtag",
    name: "ri-user-line",
    passport: "ri-passport-line",
    room: "ri-hotel-bed-line",
    phone: "ri-phone-line",
    email: "ri-mail-line",
    other: "ri-search-line",
  }[f];
}
